import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, forwardRef } from "react";
import Modal from "react-bootstrap/Modal";
import { Viewicon, XCircleblack } from "../../../Components/IconComponents";
import { toast } from "react-toastify";
import Table from "../../../Components/UiComponents/Table/Table";
import Loader from "react-spinner-loader";
import {
  getData,
  postDataWithBody,
  putDataWithBody,
} from "../../../Services/Services";
import {
  deleteSopById,
  getAllBankBranchListByRole,
  getThemeURL,
} from "../../../Services/EndPoints";
import { useDispatch } from "react-redux";
import { setTableData } from "../../../CentralStore/ExportLMSTable/dataSlice";
import Confirmpopup from "../Common/ConfirmPopup/Confirmpopup";
import { useTranslation } from "react-i18next";
import { RoleContext } from "../../..";
import { Button } from "react-bootstrap";
import Expandaletablebranches from "./ExpandableTableBranches";

// Component for displaying Branch details in a tabular view
const AllBranchesList = forwardRef((props) => {
  const { roleName } = useContext(RoleContext);
  const dispatch = useDispatch();
  const [SOPData, setSOPData] = useState([]);
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [loader, setLoader] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [branchesCount, setBranchesCount] = useState(0);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [filteredSOPData, setFilteredSOPData] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // User permissions state - only checking for view permission
  const [userPermissions, setUserPermissions] = useState({
    canView: false,
  });

  const navigate = useNavigate();

  // Parse web permissions from user profile - Updated for branches (permission ID: 5)
  const parseUserPermissions = (permissions) => {
    console.log("🔍 [DEBUG] Received permissions array:", permissions);

    if (!permissions || !Array.isArray(permissions)) {
      console.log("❌ [ERROR] Permissions is not an array");
      return { canView: false, canEdit: false, canDelete: false };
    }

    // Find the permission with ID 5 for "All Branches" functionality
    const branchPermission = permissions.find(
      (permission) => permission.id === 5
    );
    console.log("🔍 [DEBUG] Branch permission found:", branchPermission);

    if (!branchPermission || !branchPermission.web_permissions) {
      console.log(
        "❌ [ERROR] No branch permission found or no web_permissions"
      );
      return { canView: false, canEdit: false, canDelete: false };
    }

    // Parse web_permissions string (e.g., "V,E,D" or "V" or "V,E")
    const webPermissions = branchPermission.web_permissions
      .split(",")
      .map((p) => p.trim());
    console.log("🔍 [DEBUG] Parsed web permissions:", webPermissions);

    const result = {
      canView: webPermissions.includes("V"),
      canEdit: webPermissions.includes("E"),
      canDelete: webPermissions.includes("D"),
    };
    console.log("✅ [SUCCESS] Final permissions result:", result);

    return result;
  };

  useEffect(() => {
    if (props.locationInstance && props.locationInstance.msg !== "") {
      toast.success("Agency has been created successfully.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000, // Close after 2 seconds...
      });
    }
    getUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    sendDataToCentralStore(searchData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData]);

  useEffect(() => {
    filterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDistrict, SOPData]);

  const filterData = () => {
    let filtered = [...SOPData];

    // Filter by district - need to check both bank and branch districts
    if (selectedDistrict) {
      filtered = filtered.filter((bank) => {
        // Check if bank district matches
        const bankDistrictMatch = bank.district === selectedDistrict;

        // Check if any branch district matches
        const branchDistrictMatch =
          bank.branchDetailsList &&
          bank.branchDetailsList.some(
            (branch) => branch.district === selectedDistrict
          );

        return bankDistrictMatch || branchDistrictMatch;
      });
    }

    setFilteredSOPData(filtered);

    // Recalculate branch count for filtered data
    const totalFilteredBranchesCount = filtered.reduce((acc, bank) => {
      return acc + (bank.branchDetailsList?.length || 0);
    }, 0);
    setBranchesCount(totalFilteredBranchesCount);
    setRowCount(filtered.length);
    sendDataToCentralStore(filtered);
  };

  // 4. Create a function to get unique districts:
  const getUniqueDistricts = () => {
    const districts = new Set();

    SOPData.forEach((bank) => {
      // Add bank district
      if (bank.district) {
        districts.add(bank.district);
      }

      // Add branch districts
      if (bank.branchDetailsList) {
        bank.branchDetailsList.forEach((branch) => {
          if (branch.district) {
            districts.add(branch.district);
          }
        });
      }
    });

    return Array.from(districts).sort();
  };

  // Get shortened Text function
  const getShortenedText = (strLength, fulltext) => {
    let abbreviatedText = "";
    if (fulltext.length > strLength) {
      abbreviatedText = fulltext.substring(0, 15) + "...";
    } else {
      abbreviatedText = fulltext;
    }
    return <span title={fulltext}>{abbreviatedText}</span>;
  };

  const hideModal = () => {
    setShow(false);
  };

  //Get user profile Api for Role based data and permissions
  const getUserProfile = async () => {
    try {
      const userProfile = await getData(getThemeURL);
      if (userProfile && userProfile.length > 0) {
        // console.log("userProfilebranch", userProfile);
        // Check if user is SUPER ADMIN
        const isSuper = userProfile[0].role_name === "SUPER ADMIN";
        setIsSuperAdmin(isSuper);
        // Parse and set user permissions
        const permissions = parseUserPermissions(userProfile[0].permissions);
        setUserPermissions(permissions);

        getSOPMasterList(userProfile[0]);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  const createPayload = (profile) => {
    let admin_role;

    if (profile.role_name === "SUPER ADMIN") {
      admin_role = "SA";
    } else if (profile.is_admin === true) {
      admin_role = "HO";
    } else {
      admin_role = "U";
    }

    return {
      admin_role: admin_role,
      bank_id: profile.bank_id || null,
      branch_id: profile.branch_id || null,
    };
  };

  //get Branches List Api Call
  const getSOPMasterList = async (profile) => {
    setLoader(true);
    const payload = createPayload(profile);
    var sopMasterList = await postDataWithBody(
      getAllBankBranchListByRole,
      payload,
      {}
    );
    let sortedList = Array.isArray(sopMasterList)
      ? sopMasterList.sort((a, b) => {
          return new Date(b.created_on) - new Date(a.created_on);
        })
      : [];

    // Transform the data to match the expected format
    const formattedData = sortedList.map((bank) => {
      return {
        ...bank.bankEntity,
        entityType: "Bank", // Add entity type for banks
        branchDetailsList: bank.branchDetailsList
          ? bank.branchDetailsList.map((branch) => ({
              ...branch,
              entityType: "Branch", // Add entity type for branches
            }))
          : [],
      };
    });
    setLoader(false);
    setSOPData(formattedData);
    const totalBranchesCount = formattedData.reduce((acc, bank) => {
      return acc + (bank.branchDetailsList?.length || 0);
    }, 0);
    setBranchesCount(totalBranchesCount);
    setRowCount(formattedData.length);
  };

  // Modified function to include entity column in exported data
  const sendDataToCentralStore = (data) => {
    if (data.length > 0) {
      // Prepare data with nested branch information
      const expandedData = [];

      data.forEach((bank) => {
        // Add the bank row first
        const bankRow = {
          Entity: "Bank", // Add Entity column
          "Bank/Branch Name": bank.bankName || "NA",
          "IFSC Code": bank.ifscCode || "NA",
          Mandal: bank.mandal || "NA",
          City: bank.city || "NA",
          District: bank.district || "NA",
          "Pin Code": bank.pinCode || "NA",
          Address: bank.addressLine1 || "NA",
          "Phone Number": bank.phoneNumber || "NA",
          Email: bank.email || "NA",
          "Record Type": "Bank",
          "Parent Bank": "", // Empty for top-level bank rows
        };
        expandedData.push(bankRow);

        // Add branch rows with reference to parent bank
        if (bank.branchDetailsList && bank.branchDetailsList.length > 0) {
          bank.branchDetailsList.forEach((branch) => {
            const branchRow = {
              Entity: "Branch", // Add Entity column
              "Bank/Branch Name": branch.branchName || "NA",
              "IFSC Code": branch.ifscCode || "NA",
              Mandal: branch.mandal || "NA",
              City: branch.city || "NA",
              District: branch.district || "NA",
              "Pin Code": branch.pinCode || "NA",
              Address: branch.addressLine1 || "NA",
              "Phone Number": branch.phoneNumber || "NA",
              Email: branch.email || "NA",
              "Record Type": "Branch",
              "Parent Bank": bank.bankName || "NA",
            };
            expandedData.push(branchRow);
          });
        }
      });

      // Define columns for the export - include all possible fields
      const columns = [
        { Header: "Entity", accessor: "Entity", label: "Entity" }, // Add Entity column
        {
          Header: "Bank Name",
          accessor: "Bank Name",
          label: "Bank/Branch Name",
        },
        { Header: "IFSC Code", accessor: "IFSC Code", label: "IFSC Code" },
        { Header: "Mandal", accessor: "Mandal", label: "Mandal" },
        { Header: "City", accessor: "City", label: "City" },
        { Header: "District", accessor: "District", label: "District" },
        { Header: "Pin Code", accessor: "Pin Code", label: "Pin Code" },
        {
          Header: "Address",
          accessor: "Address",
          label: "Address",
        },
        {
          Header: "Phone Number",
          accessor: "Phone Number",
          label: "Phone Number",
        },
        { Header: "Email", accessor: "Email", label: "Email" },
      ];

      dispatch(
        setTableData({
          columns,
          tableData: expandedData,
        })
      );
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: t("branchestable.bankname"),
        accessor: "bankName",
        Cell: (props) => {
          const [showModal, setShowModal] = useState(false);
          const [fullDescription, setFullDescription] = useState("");
          const [highlightedDescription, setHighlightedDescription] =
            useState("");

          const handleShowModal = (description) => {
            setFullDescription(description);
            setHighlightedDescription(description);
            setShowModal(true);
          };

          const handleCloseModal = () => {
            setShowModal(false);
            setHighlightedDescription("");
          };

          const descriptionText = props.value || "NA";
          const truncatedDescription = getShortenedText(15, descriptionText);

          const isHighlighted = descriptionText === highlightedDescription;
          if (showModal) {
            setTimeout(() => {
              let el = document.querySelector(".modal-content");
              el.style.cssText =
                "background-color: #1c2038 !important; border-radius: 20px !important;";
            }, 0);
          }

          return (
            <span>
              <span
                title={descriptionText}
                style={{
                  cursor: "pointer",
                  backgroundColor: isHighlighted ? "#1a7a73" : "transparent",
                  padding: "2px 5px",
                  borderRadius: "5px",
                }}
                onClick={() => handleShowModal(descriptionText)}
              >
                {truncatedDescription}
              </span>

              <Modal
                show={showModal}
                onHide={handleCloseModal}
                dialogClassName="custom-modal"
                centered
                style={{
                  borderRadius: "20px",
                  border: "none",
                  top: "20%",
                }}
              >
                <Modal.Header
                  style={{
                    backgroundColor: "#1a7a73",
                    color: "white",
                    borderBottom: "none",
                  }}
                >
                  <Modal.Title style={{ color: "white", marginRight: "200px" }}>
                    Full Description
                  </Modal.Title>
                  <XCircleblack
                    onClick={handleCloseModal}
                    width="1.5em"
                    height="1.5em"
                    className="cursor-pointer"
                  />
                </Modal.Header>
                <Modal.Body
                  style={{
                    backgroundColor: "#fcfcfc",
                    color: "#001b30",
                    border: "none",
                    borderRadius: "0px",
                    padding: "20px",
                    lineHeight: "1.8",
                  }}
                >
                  <p>{fullDescription}</p>
                </Modal.Body>
                <Modal.Footer
                  style={{
                    backgroundColor: "#fcfcfc",
                    color: "white",
                    borderTop: "none",
                  }}
                >
                  <Button
                    variant="secondary"
                    onClick={handleCloseModal}
                    style={{
                      backgroundColor: "#1a7a73",
                      borderColor: "#003366",
                      borderRadius: "5px",
                    }}
                  >
                    Close
                  </Button>
                </Modal.Footer>
              </Modal>
            </span>
          );
        },
        disableSortBy: true,
      },
      {
        Header: t("branchestable.ifsccode"),
        accessor: "ifscCode",
        Cell: (props) => {
          if (props && props.value) {
            if (props.value.length > 0) {
              return getShortenedText(15, props.value);
            }
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
      },
      {
        Header: t("branchestable.mandal"),
        accessor: "mandal",
        disableSortBy: true,
      },
      {
        Header: t("branchestable.city"),
        accessor: "city",
        disableSortBy: true,
        sortType: (rowA, rowB, columnId) => {
          const dateA = new Date(rowA.values[columnId]);
          const dateB = new Date(rowB.values[columnId]);
          return dateB - dateA;
        },
      },
      {
        Header: t("branchestable.district"),
        accessor: "district",
        Cell: (props) => {
          if (props && props.value) {
            if (props.value.length > 0) {
              return getShortenedText(15, props.value);
            }
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
      },
      {
        Header: t("Phone Number"),
        accessor: "phoneNumber",
        disableSortBy: true,
      },
      {
        Header: t("Email Address"),
        accessor: "email",
        disableSortBy: true,
      },
      // Only show actions column if user has view permission
      ...(userPermissions.canView
        ? [
            {
              Header: t("branchestable.action"),
              disableSortBy: true,
              Cell: (props) => {
                const [isdelete, setIsdelete] = useState(false);
                const deletePopUp = () => {
                  setIsdelete(!isdelete);
                };
                const deleteTableDataById = async (id) => {
                  let data = { id: id };
                  var res = await putDataWithBody(deleteSopById, data, {});
                  if (res.status === "success") {
                    getSOPMasterList();
                  }
                  toast.success(res.msg, {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                  });
                  setIsdelete(false);
                };

                return (
                  <div className="usertable__actionsicon">
                    {/* View button - only show if user has view permission */}
                    {userPermissions.canView && (
                      <span
                        onClick={() =>
                          navigate("/branches/view-branch-details", {
                            state: { data: props.row.original },
                            device_type: true,
                          })
                        }
                      >
                        <Viewicon
                          value={props.value}
                          className="cursor-pointer me-2"
                        />
                      </span>
                    )}

                    {isdelete && (
                      <Confirmpopup
                        className="DeltePopup"
                        show={isdelete}
                        title={t("deletepopuptext")}
                        message={t("deletepopupmessage")}
                        handleToggle={deletePopUp}
                        row={props.row}
                        name={props.row.original.task}
                        deleteRow={deleteTableDataById}
                        cancel={t("cancelbutton")}
                        action={t("actionbutton")}
                      />
                    )}
                  </div>
                );
              },
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, roleName, userPermissions] // Add userPermissions as dependency
  );

  return (
    <div className="create-user">
      <Loader
        show={loader}
        type="body"
        stack="vertical"
        message="Loading Data"
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            marginBottom: !isSuperAdmin ? "-50pt" : "-70pt",
          }}
        >
          <div
            style={{
              backgroundColor: "#1a7a73",
              color: "#fff",
              padding: "6px 12px",
              borderRadius: "4px",
              fontWeight: "bold",
              marginRight: "39px",
              marginBottom: "-106px",
              marginLeft: "20px",
              width: "140px",
              fontSize: "12px",
              height: "30px",
            }}
          >
            Total Branches: {branchesCount}
          </div>

          {/* District filter dropdown - Only show for SUPER ADMIN */}
          {isSuperAdmin && (
            <div
              className="d-flex align-items-center"
              style={{ gap: "5px", marginLeft: "390px", zIndex: "1" }}
            >
              <label
                style={{
                  width: "60px",
                  color: "#969FA8",
                  fontSize: "14px",
                  fontFamily: "Poppins",
                  fontWeight: "400",
                }}
              >
                Sort by:
              </label>
              <select
                className="pl-4 pr-8 border appearance-none text-sm w-36"
                value={selectedDistrict}
                onChange={(e) => setSelectedDistrict(e.target.value)}
                style={{
                  color: "#000000",
                  fontWeight: "400",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  height: "29px",
                  borderRadius: "4px",
                }}
              >
                <option value="">All Districts</option>
                {getUniqueDistricts().map((district, index) => (
                  <option key={index} value={district}>
                    {district}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="datatable__container">
        <Expandaletablebranches
          data={
            filteredSOPData.length > 0 || selectedDistrict
              ? filteredSOPData
              : SOPData
          }
          columns={columns}
          setSearchData={setSearchData}
          numberOfRows={10}
          showExport={false}
          showFilter={false}
          showReset={false}
          onExpand={false}
          rowCount={rowCount}
          headerTableHeadStyle={{ background: "#1a7a73", color: "#FFFFFF" }}
          tableStyle={{
            border: "1px solid # #f0f0f0",
            borderRadius: "6px",
          }}
        />
      </div>

      <Modal show={show} size="lg" className="table__modal__popup" centered>
        <Modal.Header>
          <Modal.Title>SOP Data</Modal.Title>
          <XCircleblack
            className="cursor-pointer"
            onClick={hideModal}
            width="1.5em"
            height="1.5em"
          />
        </Modal.Header>
        <Modal.Body>
          <div className="create__resource--popup">
            <div class="form-group">
              <Table
                data={SOPData}
                columns={columns}
                numberOfRows={5}
                showExport={false}
                showFilter={false}
                showReset={false}
                onExpand={false}
                headerTableHeadStyle={{
                  background: "#1a7a73",
                  color: "#FFFFFF",
                }}
                tableStyle={{ border: "none", padding: "0px" }}
              />
            </div>
            <div class="form-group">
              <Table
                data={SOPData}
                columns={columns}
                numberOfRows={5}
                showExport={false}
                showFilter={false}
                showReset={false}
                onExpand={false}
                headerTableHeadStyle={{
                  background: "#1a7a73",
                  color: "#FFFFFF",
                }}
                tableStyle={{ border: "none", padding: "0px" }}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer></Modal.Footer>
      </Modal>
    </div>
  );
});

export default AllBranchesList;
